import React, { useState, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { MdClose } from "react-icons/md";
import { CalendarIcon } from "@heroicons/react/solid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LeavesUtilised from "../sections/leaves/LeavesUtilised";
import { Button } from "../ui";

const UtilizedLeavesModal = ({
  isOpen,
  onClose,
  companyEmployeeId,
  initialYear,
}) => {
  const defaultDate =
    initialYear instanceof Date
      ? initialYear
      : initialYear
      ? new Date(Number(initialYear), 0, 1)
      : new Date();

  const [selectedYear, setSelectedYear] = useState(defaultDate);
  const closeButtonRef = useRef(null);

  const handleClose = () => {
    setSelectedYear(defaultDate);
    onClose();
  };

  const displayYear =
    selectedYear instanceof Date
      ? selectedYear.getFullYear()
      : selectedYear || new Date().getFullYear();

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      initialFocus={closeButtonRef}
      className="fixed inset-0 z-[1002] overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-6">
        <Dialog.Overlay className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />

        <div className="relative transform rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 w-full max-w-4xl border border-gray-100 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-50 via-white to-gray-50 px-6 py-5 border-b border-gray-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-t-2xl">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-primary shadow-sm ring-1 ring-blue-100 shrink-0">
                <CalendarIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <Dialog.Title className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                  Utilized Leaves
                </Dialog.Title>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                  Detailed record of leaves redeemed and deducted in attendance
                </p>
              </div>
            </div>

            {/* Year Picker & Close */}
            <div className="flex items-center gap-3 self-end sm:self-auto">
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider select-none">
                  Year:
                </span>

                <DatePicker
                  selected={selectedYear}
                  onChange={(date) => {
                    if (date) setSelectedYear(date);
                  }}
                  showYearPicker
                  dateFormat="yyyy"
                  popperProps={{
                    strategy: "fixed",
                  }}
                  popperPlacement="bottom-end"
                  popperClassName="!z-[1050]"
                  wrapperClassName="inline-flex items-center shrink-0"
                  className="input w-24 text-center font-semibold cursor-pointer bg-white"
                />
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
                aria-label="Close modal"
              >
                <MdClose className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content Body */}
          <div className="px-6 py-5 max-h-[72vh] overflow-y-auto">
            {isOpen && (
              <LeavesUtilised
                currentYear={selectedYear}
                companyEmployeeId={companyEmployeeId}
              />
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3.5 border-t border-gray-200/80 flex items-center justify-between rounded-b-2xl">
            <span className="text-xs text-gray-500">
              Showing leave records for year{" "}
              <strong className="text-gray-700">{displayYear}</strong>
            </span>
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-gray-300 hover:bg-white text-gray-700 font-medium px-5"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default UtilizedLeavesModal;
